/**
 * Shaping of a product's notes for the NOTES page (Monday 12790297504 /
 * 13028234765).
 *
 * Jaron, #development-dpt 2026-09-11 01:02: "I want everything to be condensed
 * in to 3 lines tops. Some of these have like 5,6,7." His marked-fine
 * reference is the contract Scope of Work, where a product's bullets sit in
 * ONE wrapping paragraph under its `qty x Product` line. The NOTES page used
 * to print one row per bullet; `condenseNoteBody` joins them the same way.
 *
 * Display-only: nothing here writes to the customer record.
 */

import { resolveAlarmSafeProductName } from './alarmSafeNotes'

/**
 * The one line `store.ts` injects into `globalNotes` when a customer has a
 * two-story home. It lives in a field the rep also types into, so the NOTES
 * page needs to tell this exact, known string apart from what a person wrote.
 */
export const TWO_STORY_FEE_NOTE = 'A $750 2-story flat fee will be charged'

/**
 * `globalNotes` with the two-story line removed, then appended once when the
 * home is two-story. Picking "2" again used to append a second copy: the
 * removal was computed and then overwritten by an append to the raw notes.
 */
export const withTwoStoryFeeNote = (
  notes: string,
  twoStory: boolean,
): string => {
  const without = notes.replace('\n' + TWO_STORY_FEE_NOTE, '')
  return twoStory ? without + '\n' + TWO_STORY_FEE_NOTE : without
}

const BULLET_PREFIX = /^\s*(?:•|-)\s*/

/**
 * Joins a product's description lines into one paragraph:
 * `["• A", "• B"]` -> `"A · B"`. Blank lines are dropped.
 */
export const condenseNoteBody = (lines: readonly string[]): string =>
  lines
    .map((l) => l.replace(BULLET_PREFIX, '').trim())
    .filter((l) => l.length > 0)
    .join(' · ')

export interface ProductNote {
  /** First line: `qty x Product`, or the note's own title. */
  readonly header: string
  /** Remaining lines condensed into a single paragraph (may be empty). */
  readonly body: string
}

/**
 * Splits a rolled-up notes blob into products.
 *
 * `multi` is for the security sections, whose calculators separate products
 * with a blank line. Every other efficiency writes ONE product's notes with
 * stray blank lines between its parts (`notesLines.push('\n')`), so it is read
 * as a single product whatever its spacing.
 */
export const toProductNotes = (
  text: string,
  multi: boolean,
): readonly ProductNote[] => {
  const blocks = multi ? text.split(/\n\s*\n/) : [text]

  return blocks
    .map((block) => block.split('\n').filter((l) => l.trim().length > 0))
    .filter((lines) => lines.length > 0)
    .map(([header, ...rest]) => {
      const productHeader = (header ?? '').trim()
      const productName = multi
        ? productHeader.match(/^\d+\s*x\s+(.+)$/i)?.[1]?.trim()
        : undefined
      const firstTitle = rest[0]
        ?.trim()
        .replace(/\s*[—–-]\s*$/, '')
        .trim()
      // Alarm-safe display rename makes S4's auto-note title duplicate its
      // header. Strip only this canonical S4 title; other matching titles may
      // be meaningful product notes and must remain.
      const normalizedProductName = productName?.toLowerCase()
      const isS4TitleDuplicate =
        normalizedProductName === 'eufy s4 wireless camera' &&
        firstTitle?.toLowerCase() === normalizedProductName
      const descriptionLines = isS4TitleDuplicate ? rest.slice(1) : rest

      return {
        body: condenseNoteBody(descriptionLines),
        header: productHeader,
      }
    })
}

/**
 * Most note items printed under EACH product in the Job Summary's Notes column
 * (Monday 13213613154, Oct 5 meeting): every product of the category is listed,
 * like before, and each one shows up to 3 of its notes; the full text stays on
 * the NOTES page. It never limits how many products are shown (Jaron, Oct 8:
 * "Where's all the equipment? Where's the notes?").
 */
export const SHORT_NOTES_MAX_LINES = 3

/** Same limit, kept under its old name for callers. */
export const SHORT_NOTES_BODY_FRAGMENTS = SHORT_NOTES_MAX_LINES

export interface ShortNoteLine {
  /**
   * `product`: product title. `detail`: its description. `custom`: typed by a
   * person (bold). `more`: the "+N more" pointer to the NOTES page.
   * `separator`: divider between products, not counted as a line.
   */
  readonly kind: 'custom' | 'detail' | 'more' | 'product' | 'separator'
  readonly text: string
}

export const S4_PRODUCT_NAME = 'eufy S4 Wireless Camera'

/** True when a `qty x Product` note header is the S4's block. */
export const isS4Header = (header: string): boolean => {
  const name = header.match(/^\d+\s*x\s+(.+)$/i)?.[1]?.trim()
  return !!name && resolveAlarmSafeProductName(name) === S4_PRODUCT_NAME
}

/**
 * The Job Summary version of one category's notes: EVERY product, each with its
 * `qty x Product` title and up to `max` of its own note items, then the rep's
 * custom lines. Alarm-safe names come from the caller's notes as before.
 *
 * `excludeS4` drops the eufy S4 block: Job Summary already prints it in its
 * own sub-row under the category.
 *
 * Display-only, like everything in this file.
 */
export const shortProductNotes = (
  auto: string,
  custom: string,
  multi: boolean,
  max = SHORT_NOTES_MAX_LINES,
  excludeS4 = false,
): readonly ShortNoteLine[] => {
  const groups: ShortNoteLine[][] = toProductNotes(auto, multi)
    .filter(({ header }) => !(excludeS4 && multi && isS4Header(header)))
    .map(({ body, header }): ShortNoteLine[] => {
      const product: ShortNoteLine = { kind: 'product', text: header }
      const items = body
        .split(' · ')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .slice(0, max)
      return [
        product,
        ...items.map((text): ShortNoteLine => ({ kind: 'detail', text })),
      ]
    })

  const customLines = custom
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((text): ShortNoteLine => ({ kind: 'custom', text }))
  if (customLines.length) groups.push(customLines)

  const result: ShortNoteLine[] = []
  for (const group of groups) {
    if (result.length) result.push({ kind: 'separator', text: '' })
    result.push(...group)
  }
  return result
}
